import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { streamTarotReading } from '../tarot-utils/readingApi';
import { localizeCard, useLocalizedSpread } from '../i18n/useLocalized';
import useNarrator from './useNarrator';

const VOICE_KEY = 'arcanos.voice';

const readVoicePreference = () => {
  try {
    return localStorage.getItem(VOICE_KEY) !== 'off';
  } catch (err) {
    return true;
  }
};

const ReadingSessionContext = createContext(null);

/** The reading in progress, or null outside a ReadingSession. */
export const useReadingSession = () => useContext(ReadingSessionContext);

/**
 * Owns one reading: streams it from the backend, keeps its sentences and
 * drives the narrator. The voice circle above the cards, the captions dock
 * and the full text below all read from here, so they always agree.
 *
 * With `cards` null there is no reading yet and nothing is fetched.
 */
export function ReadingSession({ cards, spread, children }) {
  // Raw text of each sentence received so far, in order.
  const [sentences, setSentences] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | loading | streaming | done | error
  const [error, setError] = useState(null);
  // Bumping this re-runs the fetch with the same drawn cards, so a failed
  // reading can be retried without shuffling or re-drawing.
  const [attempt, setAttempt] = useState(0);
  const [voiceOn, setVoiceOn] = useState(readVoicePreference);
  // Whether the reading on screen is being narrated. Turning the voice off
  // ends that at once; turning it on takes effect from the next reading.
  const [narrated, setNarrated] = useState(false);
  // Whether the big voice circle is on screen; when it is not, the dock
  // shows a small one so the voice never goes out of sight.
  const [haloVisible, setHaloVisible] = useState(false);
  const voiceOnRef = useRef(voiceOn);
  const readingRef = useRef(null);
  const { i18n } = useTranslation();
  const narrator = useNarrator();
  const { enqueue, reset } = narrator;

  const localizedSpread = useLocalizedSpread(spread);
  const spreadName = localizedSpread?.name;
  // Cards are drawn in position order, so card i sits in position i.
  const positionNames = (localizedSpread?.positions || []).map(position => position.name).join('\n');

  useEffect(() => {
    voiceOnRef.current = voiceOn;
    try {
      localStorage.setItem(VOICE_KEY, voiceOn ? 'on' : 'off');
    } catch (err) {
      // Storage blocked (private mode): the choice just isn't remembered.
    }
  }, [voiceOn]);

  useEffect(() => {
    reset();
    setSentences([]);
    setError(null);

    if (!cards) {
      setNarrated(false);
      setStatus('idle');
      return undefined;
    }

    const controller = new AbortController();
    const voice = voiceOnRef.current && narrator.supported;
    setNarrated(voice);
    setStatus('loading');

    // Send the card names already translated, so the model reasons about
    // "El Loco" rather than translating "The Fool" on the fly.
    // Each card also carries its position ("Past", "Obstáculo"...), which
    // is what gives the card its meaning in the spread.
    const positions = positionNames.split('\n');
    const localizedCards = cards.map((card, index) => ({
      ...localizeCard(card, i18n.language),
      position: positions[index]
    }));

    streamTarotReading(
      { cards: localizedCards, spreadName, language: i18n.language, voice },
      {
        signal: controller.signal,
        onSentence: (sentence) => {
          setStatus('streaming');
          setSentences(prev => [...prev, sentence.raw]);
          if (voice) enqueue(sentence);
        }
      }
    )
      .then(() => setStatus('done'))
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('Failed to generate reading:', err);
        setError(err.message);
        setStatus('error');
      });

    return () => controller.abort();
    // narrator.supported never changes after mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, spreadName, positionNames, i18n.language, attempt, enqueue, reset]);

  const value = useMemo(() => ({
    active: Boolean(cards),
    sentences,
    status,
    error,
    narrated,
    narrator,
    voiceOn,
    readingRef,
    haloVisible,
    setHaloVisible,
    retry: () => setAttempt(a => a + 1),
    toggleVoice: () => {
      if (voiceOnRef.current && narrated) {
        reset();
        setNarrated(false);
      }
      setVoiceOn(on => !on);
    }
  }), [cards, sentences, status, error, narrated, narrator, voiceOn, haloVisible, reset]);

  return (
    <ReadingSessionContext.Provider value={value}>
      {children}
    </ReadingSessionContext.Provider>
  );
}

/** Whether a reading started now would be narrated. */
export const willNarrate = () =>
  readVoicePreference() && Boolean(window.AudioContext || window.webkitAudioContext);
